import { cn } from '@askable/ui/lib/utils';
import Intercom, { shutdown, update } from '@intercom/messenger-js-sdk';
import { useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation } from 'src/lib/routing';

import { withAppContext } from 'components/HOCS';
import { ImpersonationBanner } from 'components/ImpersonationBanner/ImpersonationBanner';
import { TeamEnforcementBanner } from 'components/TeamEnforcementBanner/TeamEnforcementBanner';
import { HeaderAds } from 'components/common';
import { MainNavSidebar } from 'components/common/MainNavSidebar';
import { useConnectedClient } from 'context/ConnectedClientContext';
import { globalVariables } from 'lib/globalVariables';
import { teamUtils } from 'lib/teams';

const INTERCOM_APP_ID = 'w5j4obnp';
const INTERCOM_APP_ID_DEV = 's9umtlvp';
const APP_ID = globalVariables.getEnvironmentVariables().environment === 'PROD' ? INTERCOM_APP_ID : INTERCOM_APP_ID_DEV;

const intercomBaseProperties = {
  app_id: APP_ID,
  // Set base to US region to match the workspace (app ID).
  // Hitting a regional fallback can result in "App not found".
  api_base: 'https://api-iam.intercom.io',
};

function App(props: any) {
  const { authState, details: clientDetails } = useConnectedClient();
  const { pathname } = useLocation();
  const isStreamsRoute = pathname.startsWith('/streams');
  const isManageBookingPage = window?.location?.pathname?.includes('/bookings') ?? false;
  const lastUserIdIntercom = useRef<(string | null) | undefined>(undefined);
  const hasIntercomInit = useRef<boolean>(false);

  const isResearcher = clientDetails?.type?.researcher;

  const [userId, intercomUser] = useMemo(() => {
    const intercomUser = {
      intercom_user_jwt: clientDetails?.intercomJWT,
      name: `${clientDetails?.name?.firstname} ${clientDetails?.name?.lastname}`.replace(/^\s+|\s+$/g, ''),
      Company: clientDetails?.ConnectedTeam?.name,
      user_type: isResearcher ? 'researcher' : 'client',
      track_quota: clientDetails?.ConnectedTeam?.products?.track_quota ?? undefined,
      hide_default_launcher: true,
    };
    return [clientDetails?.id, intercomUser];
  }, [clientDetails]);

  // Init Intercom
  useEffect(() => {
    if (!globalVariables.getEnvironmentVariables().enableIntercom) {
      shutdown();
      lastUserIdIntercom.current = undefined;
      hasIntercomInit.current = false;
      return;
    }

    if (authState !== 'authenticated') {
      shutdown();
      lastUserIdIntercom.current = undefined;
      hasIntercomInit.current = false;
      return;
    }

    if (!intercomUser.intercom_user_jwt) {
      shutdown();
      lastUserIdIntercom.current = undefined;
      hasIntercomInit.current = false;
      return;
    }

    if (lastUserIdIntercom.current === undefined) {
      Intercom({
        ...intercomBaseProperties,
        ...intercomUser,
      });
      lastUserIdIntercom.current = userId;
      hasIntercomInit.current = true;
      return;
    }

    if (lastUserIdIntercom.current !== userId) {
      shutdown();
      Intercom({
        ...intercomBaseProperties,
        ...intercomUser,
      });
      lastUserIdIntercom.current = userId;
      hasIntercomInit.current = true;
      return;
    }

    if (hasIntercomInit.current) {
      update(intercomUser);
    }
  }, [authState, intercomUser, userId]);

  // Store the teams' currency code and symbol in state
  const teamTax = clientDetails?.ConnectedTeam?.settings?.billing?.tax ?? undefined;
  const teamCurrency = clientDetails?.ConnectedTeam?.settings?.billing?.currency ?? undefined;

  useEffect(() => {
    teamUtils.save({ tax: teamTax, currency: teamCurrency });
  }, [teamTax, teamCurrency]);

  const isNewStudiesAndPlusProjectsTablePage =
    window.location.pathname === '/studies' || window.location.pathname === '/plus-projects';

  return (
    <div className={cn('flex h-full', isStreamsRoute ? 'bg-background' : 'bg-sidebar')}>
      {authState === 'authenticated' && !isStreamsRoute ? <MainNavSidebar /> : null}
      <main
        // The nav rail is the ground; everything else sits on a card inset from
        // it. Only from md up — below that the rail is an overlay, so there's
        // nothing for the card to sit on.
        // `!h-auto` so flex stretch sizes the card around its margins —
        // `.appRoot`'s `height: 100%` would otherwise push the bottom edge, and
        // its rounded corners, past the viewport.
        //
        // Streams opts out of the whole treatment: it renders no rail, so the
        // card would be inset from nothing — flush on one edge and gapped on
        // three. Its theme defines neither --canvas nor --sidebar either, so
        // both fills would resolve to nothing there.
        className={cn('appRoot', {
          'bg-canvas md:my-2 md:mr-2 md:!h-auto md:rounded-xl md:border-0.5 md:border-border-subtle md:shadow-raised':
            !isStreamsRoute,
          'bg-background': isStreamsRoute,
          'block-overscroll-behaviour': isManageBookingPage,
          'flex flex-1 flex-col': isNewStudiesAndPlusProjectsTablePage,
        })}
        id="main-content"
      >
        <ImpersonationBanner />
        <TeamEnforcementBanner />
        {authState === 'authenticated' ? <HeaderAds /> : null}
        {props.children}
        <Outlet />
      </main>
    </div>
  );
}

export default withAppContext(App);
